import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Apartment } from '../types/Apartment';
import { useApartments } from '../context/ApartmentContext';
import { formatPrice } from '../config/priceConfig';

const Container = styled.div`
  padding: 24px;
  max-height: calc(100vh - 48px);
  overflow-y: auto;
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const Title = styled.h2`
  margin: 0;
  color: #2c3e50;
  font-size: 24px;
  font-weight: 600;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'success' | 'danger' }>`
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: #667eea;
          color: white;
          &:hover { background: #5a6fd8; }
        `;
      case 'secondary':
        return `
          background: #95a5a6;
          color: white;
          &:hover { background: #7f8c8d; }
        `;
      case 'success':
        return `
          background: #2ecc71;
          color: white;
          &:hover { background: #27ae60; }
        `;
      case 'danger':
        return `
          background: #e74c3c;
          color: white;
          &:hover { background: #c0392b; }
        `;
      default:
        return `
          background: #ecf0f1;
          color: #2c3e50;
          &:hover { background: #d5dbdb; }
        `;
    }
  }}
`;

const ApartmentsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const ApartmentCard = styled.div<{ isSelected: boolean }>`
  padding: 16px;
  border: 2px solid ${props => props.isSelected ? '#667eea' : '#e1e5e9'};
  border-radius: 12px;
  background: ${props => props.isSelected ? '#f8f9ff' : 'white'};
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: #667eea;
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(102, 126, 234, 0.1);
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const CardTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
`;

const CardPrice = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #667eea;
`;

const CardDetails = styled.div`
  font-size: 14px;
  color: #7f8c8d;
`;

const EditForm = styled.form`
  background: white;
  border: 2px solid #e1e5e9;
  border-radius: 12px;
  padding: 24px;
  margin-top: 24px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
  margin-bottom: 16px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  margin-bottom: 8px;
  font-weight: 600;
  color: #2c3e50;
  font-size: 14px;
`;

const Input = styled.input`
  padding: 12px 16px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const TextArea = styled.textarea`
  padding: 12px 16px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 14px;
  min-height: 80px;
  resize: vertical;
  font-family: inherit;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const Select = styled.select`
  padding: 12px 16px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  accent-color: #667eea;
`;

const FeaturesContainer = styled.div`
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  padding: 16px;
  background: #f8f9fa;
`;

const FeaturesList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
`;

const FeatureTag = styled.span`
  background: #667eea;
  color: white;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const RemoveFeatureButton = styled.button`
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  font-size: 14px;
  padding: 0;
  display: flex;
  align-items: center;
  
  &:hover {
    opacity: 0.8;
  }
`;

const AddFeatureContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-end;
`;

const SuccessMessage = styled.div`
  background: #d5edda;
  color: #155724;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 16px;
  border: 1px solid #c3e6cb;
`;

const ErrorMessage = styled.div`
  background: #f8d7da;
  color: #721c24;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 16px;
  border: 1px solid #f5c6cb;
`;

interface ApartmentDetailsManagerProps {
  onBack: () => void;
}

const ApartmentDetailsManager: React.FC<ApartmentDetailsManagerProps> = ({ onBack }) => {
  const { apartments, updateApartment } = useApartments();
  const [selectedApartment, setSelectedApartment] = useState<Apartment | null>(null);
  const [editedApartment, setEditedApartment] = useState<Apartment | null>(null);
  const [newFeature, setNewFeature] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (selectedApartment) {
      setEditedApartment({ ...selectedApartment });
    }
  }, [selectedApartment]);

  const handleApartmentSelect = (apartment: Apartment) => {
    setSelectedApartment(apartment);
    setMessage(null);
  };

  const handleInputChange = (field: keyof Apartment, value: any) => {
    if (!editedApartment) return;
    
    setEditedApartment({
      ...editedApartment,
      [field]: value
    });
  };

  const handleAddFeature = () => {
    if (!editedApartment || !newFeature.trim()) return;
    
    const currentFeatures = editedApartment.features || [];
    setEditedApartment({
      ...editedApartment,
      features: [...currentFeatures, newFeature.trim()]
    });
    setNewFeature('');
  };

  const handleRemoveFeature = (index: number) => {
    if (!editedApartment || !editedApartment.features) return;
    
    setEditedApartment({
      ...editedApartment,
      features: editedApartment.features.filter((_, i) => i !== index)
    });
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editedApartment) return;

    try {
      await updateApartment(editedApartment);
      setMessage({ type: 'success', text: 'Wohnung erfolgreich aktualisiert!' });
      
      // Update the selected apartment with new data
      setSelectedApartment(editedApartment);
      
      // Clear message after 3 seconds
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Fehler beim Speichern der Wohnung.' });
    }
  };

  const handleCancel = () => {
    if (selectedApartment) {
      setEditedApartment({ ...selectedApartment });
      setMessage(null);
    }
  };

  return (
    <Container>
      <HeaderSection>
        <Title>Wohnungsdetails verwalten</Title>
        <Button variant="secondary" onClick={onBack}>
          ← Zurück zur Übersicht
        </Button>
      </HeaderSection>

      <ApartmentsList>
        {apartments && apartments.map((apartment) => (
          <ApartmentCard
            key={apartment.id}
            isSelected={selectedApartment?.id === apartment.id}
            onClick={() => handleApartmentSelect(apartment)}
          >
            <CardHeader>
              <CardTitle>{apartment.name}</CardTitle>
              <CardPrice>{formatPrice(apartment.price)}</CardPrice>
            </CardHeader>
            <CardDetails>
              {apartment.size}m² • {apartment.rooms} Zimmer • {apartment.floor === 0 ? 'EG' : `${apartment.floor}. OG`}
            </CardDetails>
          </ApartmentCard>
        ))}
      </ApartmentsList>

      {selectedApartment && editedApartment && (
        <EditForm onSubmit={handleSave}>
          <HeaderSection>
            <Title>Details bearbeiten: {selectedApartment.name}</Title>
            <ButtonGroup>
              <Button type="button" onClick={handleCancel}>
                Abbrechen
              </Button>
              <Button type="submit" variant="success">
                Speichern
              </Button>
            </ButtonGroup>
          </HeaderSection>

          {message && (
            message.type === 'success' ? (
              <SuccessMessage>{message.text}</SuccessMessage>
            ) : (
              <ErrorMessage>{message.text}</ErrorMessage>
            )
          )}

          <FormRow>
            <FormGroup>
              <Label>Name</Label>
              <Input
                type="text"
                value={editedApartment.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>Adresse</Label>
              <Input
                type="text"
                value={editedApartment.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                required
              />
            </FormGroup>
          </FormRow>

          <FormRow>
            <FormGroup>
              <Label>Preis (CHF)</Label>
              <Input
                type="number"
                value={editedApartment.price}
                onChange={(e) => handleInputChange('price', parseInt(e.target.value) || 0)}
                min="0"
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>Größe (m²)</Label>
              <Input
                type="number"
                value={editedApartment.size}
                onChange={(e) => handleInputChange('size', parseInt(e.target.value) || 0)}
                min="0"
                required
              />
            </FormGroup>
          </FormRow>

          <FormRow>
            <FormGroup>
              <Label>Zimmeranzahl</Label>
              <Input
                type="number"
                step="0.5"
                value={editedApartment.rooms}
                onChange={(e) => handleInputChange('rooms', parseFloat(e.target.value) || 0)}
                min="0"
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>Etage</Label>
              <Select
                value={editedApartment.floor}
                onChange={(e) => handleInputChange('floor', parseInt(e.target.value))}
                required
              >
                <option value={0}>EG</option>
                <option value={1}>1. Obergeschoss</option>
                <option value={2}>2. Obergeschoss</option>
                <option value={3}>Attika</option>
              </Select>
            </FormGroup>
          </FormRow>

          <FormRow>
            <FormGroup>
              <Label>Bezug ab</Label>
              <Input
                type="text"
                value={editedApartment.constructionYear || ''}
                onChange={(e) => handleInputChange('constructionYear', e.target.value || undefined)}
                placeholder="z.B. Frühjahr 2027"
              />
            </FormGroup>
            <FormGroup>
              <Label>Heizungstyp</Label>
              <Select
                value={editedApartment.heatingType || ''}
                onChange={(e) => handleInputChange('heatingType', e.target.value || undefined)}
              >
                <option value="">Nicht angegeben</option>
                <option value="Fernwärme">Fernwärme</option>
                <option value="Gas">Gas</option>
                <option value="Öl">Öl</option>
                <option value="Wärmepumpe">Wärmepumpe</option>
                <option value="Elektro">Elektro</option>
              </Select>
            </FormGroup>
          </FormRow>

          <FormRow>
            <FormGroup>
              <CheckboxGroup>
                <Checkbox
                  type="checkbox"
                  checked={editedApartment.parking || false}
                  onChange={(e) => handleInputChange('parking', e.target.checked)}
                />
                <Label>Parkplatz vorhanden</Label>
              </CheckboxGroup>
            </FormGroup>
            <FormGroup>
              <CheckboxGroup>
                <Checkbox
                  type="checkbox"
                  checked={editedApartment.balcony || false}
                  onChange={(e) => handleInputChange('balcony', e.target.checked)}
                />
                <Label>Balkon vorhanden</Label>
              </CheckboxGroup>
            </FormGroup>
          </FormRow>

          <FormRow>
            <FormGroup>
              <CheckboxGroup>
                <Checkbox
                  type="checkbox"
                  checked={editedApartment.elevator || false}
                  onChange={(e) => handleInputChange('elevator', e.target.checked)}
                />
                <Label>Aufzug vorhanden</Label>
              </CheckboxGroup>
            </FormGroup>
            <FormGroup>
              <CheckboxGroup>
                <Checkbox
                  type="checkbox"
                  checked={editedApartment.available}
                  onChange={(e) => handleInputChange('available', e.target.checked)}
                />
                <Label>Verfügbar</Label>
              </CheckboxGroup>
            </FormGroup>
          </FormRow>

          <FormGroup>
            <Label>Beschreibung</Label>
            <TextArea
              value={editedApartment.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Detaillierte Beschreibung der Wohnung..."
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>Ausstattungsmerkmale</Label>
            <FeaturesContainer>
              <FeaturesList>
                {editedApartment.features && editedApartment.features.map((feature, index) => (
                  <FeatureTag key={index}>
                    {feature}
                    <RemoveFeatureButton
                      type="button"
                      onClick={() => handleRemoveFeature(index)}
                    >
                      ×
                    </RemoveFeatureButton>
                  </FeatureTag>
                ))}
              </FeaturesList>
              <AddFeatureContainer>
                <Input
                  type="text"
                  placeholder="Neues Merkmal hinzufügen..."
                  value={newFeature}
                  onChange={(e) => setNewFeature(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddFeature())}
                />
                <Button type="button" onClick={handleAddFeature} disabled={!newFeature.trim()}>
                  Hinzufügen
                </Button>
              </AddFeatureContainer>
            </FeaturesContainer>
          </FormGroup>

          <FormRow>
            <FormGroup>
              <Label>Vorschaubild URL</Label>
              <Input
                type="url"
                value={editedApartment.thumbnail}
                onChange={(e) => handleInputChange('thumbnail', e.target.value)}
                placeholder="https://..."
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>Grundriss PDF URL</Label>
              <Input
                type="url"
                value={editedApartment.floorPlan}
                onChange={(e) => handleInputChange('floorPlan', e.target.value)}
                placeholder="https://..."
                required
              />
            </FormGroup>
          </FormRow>
        </EditForm>
      )}
    </Container>
  );
};

export default ApartmentDetailsManager; 